import React from 'react';
import * as d3 from 'd3';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { useAsyncLayout } from '../pedigree-tool/useAsyncLayout';
import { config } from '../pedigree-tool/pedigree/config';
import { nodeSymbolType } from '../pedigree-tool/pedigree/reproSymbol';
import { toLayoutFamily } from './sketchFamily';
import { conditionStatus } from './generatePedigree';
import { CHROME, OTHER_FILL, PREVIEW_HEIGHT } from './tokens';
import { useAutoTranslation } from 'src/hooks/useTranslate';

const PREVIEW_PREFS = Object.freeze({
  symbol_size: 155,
  horizontal_spacing: 90,
  vertical_spacing: 55
});

const SYMBOL_SIZE = PREVIEW_PREFS.symbol_size;
const SYMBOL_AREA = SYMBOL_SIZE * SYMBOL_SIZE;
const PAD = 300;
const STROKE_PX = 1.15;
const NAME_PX = 9.5;
const PID_PX = 8;
const FALLBACK_SCALE = 0.11;
const ZOOM_OUT_FACTOR = 0.7;
const ZOOM_IN_FACTOR = 4;
const WHEEL_SENSITIVITY = 1 / 1400;

const symbolPath = d3.symbol();

// UNION nodes are the engine's marriage junctions, not people.
const isPerson = (n) => n && n.kind !== config.NODE_TYPE.UNION && !n.hidden;

function bounds(nodes) {
  const xs = nodes.map((n) => n.x).filter(Number.isFinite);
  const ys = nodes.map((n) => n.y).filter(Number.isFinite);
  if (!xs.length) return null;
  return {
    minX: Math.min(...xs) - PAD,
    maxX: Math.max(...xs) + PAD,
    minY: Math.min(...ys) - PAD,
    maxY: Math.max(...ys) + PAD + SYMBOL_SIZE
  };
}

export default function SketchPreview({
  config: sketch,
  conditions = null,
  choices = null,
  height = PREVIEW_HEIGHT
}) {
  const t = useAutoTranslation();

  const runLayout = useAsyncLayout();
  const [layout, setLayout] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  // Text is divided by this so a name lands at a fixed pixel size.
  const [scale, setScale] = React.useState(null);
  const reqRef = React.useRef(0);
  const svgRef = React.useRef(null);
  const worldRef = React.useRef(null);
  const zoomRef = React.useRef(null);
  // True once the user has panned or zoomed, which turns off the auto-fit.
  const userMovedRef = React.useRef(false);

  const family = React.useMemo(
    () =>
      toLayoutFamily(sketch, (term) =>
        conditionStatus(term, conditions, choices)
      ),
    [sketch, conditions, choices]
  );

  React.useEffect(() => {
    if (!family.length) return;
    const reqId = ++reqRef.current;
    setLayout(null);
    setFailed(false);
    runLayout(
      {
        reqId,
        family,
        // No saved arrangement can exist: these uids are a moment old.
        familyId: 'sketch-preview',
        probandId: null,
        customPositions: null,
        establishedOrder: null,
        orientationSeed: null,
        prefs: PREVIEW_PREFS,
        spacing: null
      },
      (res) => {
        if (res.reqId !== reqRef.current) return; // superseded
        if (res.ok) setLayout({ nodes: res.nodes, edges: res.edges });
        else setFailed(true);
      }
    );
  }, [family, runLayout]);

  // Kept across relayouts so a zoomed-in user is not thrown back out.
  React.useEffect(() => {
    if (!svgRef.current || !worldRef.current) return undefined;
    const svg = d3.select(svgRef.current);
    const world = d3.select(worldRef.current);
    const zoom = d3
      .zoom()
      // Real limits are set by the fit, the only place that knows the scale.
      .scaleExtent([0, Infinity])
      .wheelDelta((event) => -event.deltaY * WHEEL_SENSITIVITY)
      .on('zoom', (event) => {
        world.attr('transform', event.transform);
        // Null for a programmatic transform, so the auto-fit knows its own.
        if (event.sourceEvent) userMovedRef.current = true;
      });
    zoomRef.current = zoom;
    svg.call(zoom).on('dblclick.zoom', null);
    return () => {
      svg.on('.zoom', null);
      zoomRef.current = null;
    };
  }, []);

  const fit = React.useCallback(() => {
    if (!layout || !svgRef.current || !zoomRef.current) return;
    const box = bounds(layout.nodes.filter(isPerson));
    if (!box) return;
    const { width: w, height: h } = svgRef.current.getBoundingClientRect();
    if (!w || !h) return; // not laid out yet (jsdom reports 0)

    const k = Math.min(w / (box.maxX - box.minX), h / (box.maxY - box.minY));
    if (!Number.isFinite(k) || k <= 0) return;
    setScale(k);

    zoomRef.current
      .scaleExtent([k * ZOOM_OUT_FACTOR, k * ZOOM_IN_FACTOR])
      // A stray drag leaving an empty box reads as a failed preview.
      .translateExtent([
        [box.minX, box.minY],
        [box.maxX, box.maxY]
      ]);

    d3.select(svgRef.current).call(
      zoomRef.current.transform,
      d3.zoomIdentity
        .translate(
          (w - (box.minX + box.maxX) * k) / 2,
          (h - (box.minY + box.maxY) * k) / 2
        )
        .scale(k)
    );
  }, [layout]);

  // Reset, not preserved: the old transform can leave a new family off-screen.
  React.useEffect(() => {
    userMovedRef.current = false;
    fit();
  }, [fit]);

  // The panel animates wider, so the first fit runs against the old width.
  React.useEffect(() => {
    if (typeof ResizeObserver === 'undefined' || !svgRef.current)
      return undefined;
    const observer = new ResizeObserver(() => {
      if (!userMovedRef.current) fit();
    });
    observer.observe(svgRef.current);
    return () => observer.disconnect();
  }, [fit]);

  const persons = layout ? layout.nodes.filter(isPerson) : [];
  // Until the first fit lands, and where the box reports zero size (jsdom).
  const textScale = scale || FALLBACK_SCALE;

  return (
    <Box
      sx={{
        position: 'relative',
        height,
        borderRadius: 1.5,
        border: `1px solid ${CHROME.border}`,
        backgroundColor: '#fff',
        overflow: 'hidden'
      }}
    >
      <Box
        component="svg"
        ref={svgRef}
        role="img"
        aria-label={t('Preview of the sketched pedigree')}
        sx={{
          width: '100%',
          height: '100%',
          display: 'block',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
          touchAction: 'none'
        }}
      >
        <g ref={worldRef}>
          <g
            fill="none"
            stroke={CHROME.text}
            strokeWidth={STROKE_PX}
            vectorEffect="non-scaling-stroke"
            strokeLinecap="square"
          >
            {layout?.edges.map((edge, i) =>
              edge.d ? (
                <path key={i} d={edge.d} vectorEffect="non-scaling-stroke" />
              ) : null
            )}
          </g>

          {persons.map((person) => {
            const affected = !!person.affected;
            return (
              <g
                key={person.uid}
                // As Canvas does: the marks a reviewer checks are addressable.
                data-sketch-pid={person.pid}
                data-affected={affected ? '' : undefined}
                data-fill={person.fill || undefined}
                data-deceased={person.isDeceased ? '' : undefined}
                transform={`translate(${person.x},${person.y})`}
              >
                <path
                  d={symbolPath
                    .type(nodeSymbolType(person))
                    .size(SYMBOL_AREA)()}
                  fill={
                    !affected
                      ? '#fff'
                      : person.fill === 'other'
                      ? OTHER_FILL
                      : CHROME.text
                  }
                  stroke={CHROME.text}
                  strokeWidth={STROKE_PX}
                  vectorEffect="non-scaling-stroke"
                />
                {person.isDeceased && (
                  // Bennett's deceased slash, the same diagonal the canvas draws.
                  <line
                    x1={-SYMBOL_SIZE * 0.78}
                    y1={SYMBOL_SIZE * 0.78}
                    x2={SYMBOL_SIZE * 0.78}
                    y2={-SYMBOL_SIZE * 0.78}
                    stroke={CHROME.text}
                    strokeWidth={STROKE_PX}
                    vectorEffect="non-scaling-stroke"
                  />
                )}
                <text
                  x={SYMBOL_SIZE * 0.62}
                  y={-SYMBOL_SIZE * 0.66}
                  fontSize={PID_PX / textScale}
                  fill={CHROME.muted}
                  textAnchor="start"
                >
                  {person.pid}
                </text>
                {person.name && (
                  <text
                    y={SYMBOL_SIZE * 0.7 + NAME_PX / textScale}
                    fontSize={NAME_PX / textScale}
                    fill={CHROME.text}
                    textAnchor="middle"
                  >
                    {person.name}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </Box>

      {!layout && !failed && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <CircularProgress size={18} sx={{ color: CHROME.muted }} />
        </Box>
      )}

      {failed && (
        // The config is still applicable — only the drawing failed.
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            px: 2,
            textAlign: 'center',
            fontSize: '0.72rem',
            color: CHROME.muted
          }}
        >
          {t('Could not draw a preview. The pedigree can still be applied.')}
        </Box>
      )}
    </Box>
  );
}
